<div content class="flex flex-col gap-4">
    @if (!itemId) {
        <app-alert
            label="translate_evaluation_alert_info_title"
            description="translate_evaluation_alert_info_description"
            mode="info"
        ></app-alert>
    }
    @if (!itemId && evaluations.length > 0) {
        <div class="flex flex-col gap-1 rounded border bg-slate-100 p-4">
            <label>{{ 'translate_select_the_evaluation' | translate }}</label>
            <ng-select
                [items]="evaluations"
                [ngModel]="selectedEvaluation"
                (ngModelChange)="setSelectedEvaluation($event)"
                bindLabel="name"
                [clearable]="false"
                [searchable]="false"
            ></ng-select>
        </div>
    }
    @if (!itemId && hasNoEvaluationDefinition) {
        <app-items-are-unavailable></app-items-are-unavailable>
    }

    <!-- Records -->
    @if (!hasNoEvaluationDefinition) {
        <app-list-loading [items]="$safeNavigationMigration(item?.records)">
            @if (isResolvingLayout) {
                <div class="flex min-h-[120px] items-center justify-center">
                    <app-loading-ring></app-loading-ring>
                </div>
            } @else {
                @if (shouldShowGroupedProjectStandards()) {
                    <div class="flex flex-col gap-3">
                        @for (matrix of groupedMatrices; track matrix) {
                            <div
                                class="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                            >
                                <div
                                    class="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700"
                                >
                                    {{ matrix.name }}
                                </div>
                                <div class="flex flex-col gap-3 p-3">
                                    @if (matrix.standards?.length) {
                                        <div
                                            class="overflow-hidden rounded-xl border border-slate-200 bg-white"
                                        >
                                            <div
                                                class="border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700"
                                            >
                                                {{
                                                    'translate_standards'
                                                        | translate
                                                }}
                                            </div>
                                            <ng-container
                                                [ngTemplateOutlet]="
                                                    groupedStandardsTable
                                                "
                                                [ngTemplateOutletContext]="{
                                                    standards: matrix.standards,
                                                }"
                                            ></ng-container>
                                        </div>
                                    }
                                    @for (group of matrix.groups; track group) {
                                        <div
                                            class="overflow-hidden rounded-xl border border-slate-200 bg-white"
                                        >
                                            <div
                                                class="border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700"
                                            >
                                                {{ group.name }}
                                            </div>
                                            <ng-container
                                                [ngTemplateOutlet]="
                                                    groupedStandardsTable
                                                "
                                                [ngTemplateOutletContext]="{
                                                    standards: group.standards,
                                                }"
                                            ></ng-container>
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                } @else {
                    <div
                        class="table-rounded-corners rounded-2xl border border-slate-200 bg-white"
                    >
                        <div class="table-responsive !overflow-visible">
                            <table>
                                <ng-container
                                    [ngTemplateOutlet]="standardsTableHead"
                                ></ng-container>
                                <tbody>
                                    @for (
                                        record of item?.records;
                                        track record;
                                        let idx = $index
                                    ) {
                                        <tr>
                                            <ng-container
                                                [ngTemplateOutlet]="
                                                    shouldUseGroupedProjectStandards()
                                                        ? projectFlatRecordRow
                                                        : legacyFlatRecordCells
                                                "
                                                [ngTemplateOutletContext]="{
                                                    record: record,
                                                    idx: idx,
                                                }"
                                            ></ng-container>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                }
            }
        </app-list-loading>
    }

    <ng-template #groupedStandardsTable let-standards="standards">
        <div class="table-responsive !overflow-visible">
            <table>
                <ng-container
                    [ngTemplateOutlet]="standardsTableHead"
                ></ng-container>

                <tbody>
                    @for (standard of standards; track standard) {
                        <tr>
                            @if (getRecordForStandard(standard); as record) {
                                <td>{{ standard.name }}</td>
                                <td>
                                    <span>{{
                                        record | evaluationRecordRangeLabel
                                    }}</span>
                                </td>
                                <td>
                                    <app-evaluation-guide-preview
                                        [guide]="standard.description"
                                        [previewLimit]="guidePreviewLimit"
                                        (openGuide)="showGuideDetails($event)"
                                    ></app-evaluation-guide-preview>
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        class="hide-arrows w-full text-center"
                                        [disabled]="mode === 'view'"
                                        [min]="getRecordMinValue(record)"
                                        [max]="getRecordMaxValue(record)"
                                        [(ngModel)]="record.value"
                                        (ngModelChange)="checkForEmptyValues()"
                                    />
                                    @if (isValueOutOfRange(record)) {
                                        <p class="mt-1 text-xs text-rose-600">
                                            {{
                                                'translate_evaluation_target_exceeded'
                                                    | translate
                                            }}
                                        </p>
                                    }
                                </td>
                                @if (!shouldHideNotes()) {
                                    <td>
                                        <textarea
                                            class="w-full"
                                            [disabled]="mode === 'view'"
                                            [(ngModel)]="record.note"
                                        ></textarea>
                                    </td>
                                }
                            }
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    </ng-template>

    <ng-template #projectFlatRecordRow let-record="record" let-idx="idx">
        <td>{{ record.name }}</td>

        <td>
            <span>{{ record | evaluationRecordRangeLabel }}</span>
        </td>

        <td>
            @if (getGuideForRecord(record); as guide) {
                <app-evaluation-guide-preview
                    [guide]="guide"
                    [previewLimit]="guidePreviewLimit"
                    (openGuide)="showGuideDetails($event)"
                ></app-evaluation-guide-preview>
            }
        </td>

        <td>
            <input
                type="number"
                class="hide-arrows w-full"
                [disabled]="mode === 'view'"
                [min]="getRecordMinValue(record)"
                [max]="getRecordMaxValue(record)"
                [(ngModel)]="item.records[idx].value"
                (ngModelChange)="checkForEmptyValues()"
            />

            @if (isValueOutOfRange(record)) {
                <p class="mt-1 text-xs text-rose-600">
                    {{ 'translate_evaluation_target_exceeded' | translate }}
                </p>
            }
        </td>

        @if (!shouldHideNotes()) {
            <td>
                <textarea
                    class="w-full"
                    [disabled]="mode === 'view'"
                    [(ngModel)]="item.records[idx].note"
                ></textarea>
            </td>
        }
    </ng-template>

    <ng-template #legacyFlatRecordCells let-record="record" let-idx="idx">
        <td>{{ record.name }}</td>

        <td>
            <span>{{ record.target ?? 0 }}</span>
        </td>

        <td>
            <ng-select
                [items]="getLegacyValueOptions(record)"
                [clearable]="false"
                [searchable]="false"
                [ngModel]="item.records[idx].value"
                (ngModelChange)="onLegacyValueChange($event, idx)"
                [disabled]="mode === 'view'"
            >
                <ng-template ng-label-tmp let-item="item">
                    <div
                        class="absolute left-0 top-0 flex h-full w-full flex-row items-center px-4"
                    >
                        <span>{{
                            item | evaluationLegacyValueLabel: record.target
                        }}</span>
                    </div>
                </ng-template>

                <ng-template ng-option-tmp let-option="item" let-index="index">
                    <div
                        class="-mx-[10px] -my-[8px] px-4 py-2 transition-all duration-200 hover:bg-slate-100"
                    >
                        <span>{{
                            option | evaluationLegacyValueLabel: record.target
                        }}</span>
                    </div>
                </ng-template>
            </ng-select>
        </td>

        @if (!shouldHideNotes()) {
            <td>
                <textarea
                    class="w-full"
                    [disabled]="mode === 'view'"
                    [(ngModel)]="item.records[idx].note"
                ></textarea>
            </td>
        }
    </ng-template>

    <ng-template #standardsTableHead>
        <thead>
            <tr>
                <th>{{ 'translate_standard' | translate }}</th>
                <th>
                    @if (shouldUseGroupedProjectStandards()) {
                        {{ 'translate_evaluation_degrees' | translate }}
                    } @else {
                        {{ 'translate_target' | translate }}
                    }
                </th>
                @if (shouldUseGroupedProjectStandards()) {
                    <th>
                        {{ 'translate_guide' | translate }}
                    </th>
                }
                <th>
                    {{ 'translate_value' | translate }}
                </th>
                @if (!shouldHideNotes()) {
                    <th>
                        {{ 'translate_notes' | translate }}
                    </th>
                }
            </tr>
        </thead>
    </ng-template>

    <!-- General notes -->
    @if (!shouldHideNotes()) {
        <div class="flex flex-col gap-1">
            <label>{{ 'translate_recommendations' | translate }}</label>
            <textarea
                *appWaitUntilLoaded="item"
                [disabled]="mode === 'view'"
                [(ngModel)]="item.note"
            ></textarea>
        </div>
    }

    <!-- Save button -->
    <div class="flex flex-row justify-center gap-4">
        @if (
            mode !== 'view' &&
            (selectedEvaluation || itemId) &&
            !hasNoEvaluationDefinition &&
            item?.records?.length
        ) {
            <button
                (click)="processUpdate()"
                [disabled]="isSubmitting || hasEmptyValue || hasValueOutOfRange"
                class="btn btn-primary flex flex-row items-center gap-2 self-center"
            >
                @if (isSubmitting) {
                    <app-loading-ring></app-loading-ring>
                }
                <i class="fa-light fa-save"></i>
                <span>{{ 'translate_save' | translate }}</span>
            </button>
        }

        <ng-container
            [ngTemplateOutlet]="
                $safeNavigationMigration(customAction?.template)
            "
            [ngTemplateOutletContext]="{
                item: $safeNavigationMigration(customAction?.item),
            }"
        ></ng-container>
    </div>
</div>
